import { resolveFoxlineEmployeeId, foxlineStaffRecordForCode } from "../employeeMatch";
import { softoneEndDateForShift } from "../shiftTimeUtils";
import { resolveEmployeePgStore } from "./employeeStore";

export const SOFTONE_SERIES = {
  SHIFT: 4002,
  SICK: 4003,
  ABSENCE: 4004,
  DAY_OFF: 4006,
  NON_WORKING: 4007,
};

export function softoneScheduleRef(storeId, weekStartISO) {
  const slug = String(storeId || "")
    .trim()
    .replace(/^PG\s+/i, "pg_")
    .replace(/\s+/g, "_")
    .toLowerCase();
  return `${slug}_${weekStartISO}`;
}

// Fallback when API unavailable (was live PG_STORE_FOXLINE_LOCATION_IDS):
// const PG_STORE_FOXLINE_LOCATION_IDS = {
//   "PG Skoufa": 1,
//   "PG Omirou": 2,
//   "PG Panormou": 800,
//   "PG Heiden": 3,
//   "PG Tsamadou": 900,
//   "PG Kolokotroni": 1100,
//   "PG Dousmani": 1201,
//   "PG Drakou": 1203,
// };

export function softoneWritesDisabled(payload) {
  return Boolean(payload?.read_only || payload?.prod_sim);
}

/** SoftOne experiment AFM — hide in Ops unless API SHIFTIQ_TESTING is on. */
export const OPS_TEST_EMPLOYEE_AFMS = new Set(["999999999"]);

export function isOpsTestEmployeeCode(code) {
  const digits = String(code || "").replace(/\D/g, "");
  return Boolean(digits) && OPS_TEST_EMPLOYEE_AFMS.has(digits);
}

/** Drop test AFMs unless SoftOne locations reported testing: true. */
export function filterOpsTestEmployees(list, { testing = false } = {}) {
  if (testing) return Array.isArray(list) ? list : [];
  const rows = Array.isArray(list) ? list : [];
  const filtered = rows.filter((row) => !isOpsTestEmployeeCode(row?.employee_code));
  // Dummy catalog: API returned only the test AFM — keep it.
  if (
    filtered.length === 0 &&
    rows.length > 0 &&
    rows.every((row) => isOpsTestEmployeeCode(row?.employee_code))
  ) {
    return rows;
  }
  return filtered;
}

/** Build store label → foxline PRJCLEAD id from GET /api/softone/locations. */
export function softoneStoreToFoxlineMap(locationsPayload) {
  const map = {};
  for (const row of locationsPayload?.locations || []) {
    const storeName = String(row?.store_name || "").trim();
    const foxlineId = Number(row?.foxline_location_id);
    if (storeName && Number.isFinite(foxlineId)) {
      map[storeName] = foxlineId;
    }
  }
  return map;
}


export function buildSoftoneShiftsFromPublishedRows(
  publishedRows,
  currentStore,
  storeToFoxlineId,
  staffPool,
  storeRecords
) {
  const hostFoxlineId = storeToFoxlineId[currentStore];
  const shifts = [];
  for (const row of publishedRows || []) {
    const rowStatus = String(row.status || "").toLowerCase();
    if (["draft", "auto_draft", "other_store", "sick", "leave"].includes(rowStatus)) continue;
    const date = String(row.rota_date || "").slice(0, 10);
    if (!date) continue;
    const employeeId = resolveFoxlineEmployeeId(staffPool, row.employee_code);
    if (employeeId == null) {
      const code = String(row.employee_code || "").trim() || "(unknown)";
      console.warn(
        `[SOFTONE] Skipping shift for employee_code ${code} on ${date}: Foxline employee_id (PRSN) not resolved`
      );
      continue;
    }

    const emp = foxlineStaffRecordForCode(staffPool, row.employee_code);
    const homeStore = emp ? resolveEmployeePgStore(emp, storeRecords) : "";
    const loanLocationId =
      homeStore && homeStore !== currentStore && hostFoxlineId != null
        ? hostFoxlineId
        : null;

    let series = null;
    let from_hour = null;
    let to_hour = null;
    let end_date = null;

    if (rowStatus === "no_work") {
      series = SOFTONE_SERIES.NON_WORKING; // 4007
    } else if (row.is_off) {
      series = SOFTONE_SERIES.DAY_OFF; // 4006
    } else if (row.shift_start && row.shift_end) {
      series = SOFTONE_SERIES.SHIFT; // 4002
      from_hour = String(row.shift_start).slice(0, 5);
      to_hour = String(row.shift_end).slice(0, 5);
      end_date = softoneEndDateForShift(date, from_hour, to_hour);
    } else {
      continue;
    }

    const item = {
      employee_id: employeeId,
      date,
      series,
      from_hour,
      to_hour,
      end_date,
    };
    if (loanLocationId != null) item.loan_location_id = loanLocationId;
    shifts.push(item);
  }
  return shifts;
}

/** Home-store sync: confirmed loan-outs billed to host store (loan_location_id = host Foxline id). */
export function buildSoftoneShiftsFromOutgoingLoans(loanRows, storeToFoxlineId, staffPool) {
  const shifts = [];
  for (const row of loanRows || []) {
    const date = String(row.date || row.loan_date || "").slice(0, 10);
    if (!date) continue;
    const employeeId = resolveFoxlineEmployeeId(staffPool, row.employee_code);
    if (employeeId == null) {
      const code = String(row.employee_code || "").trim() || "(unknown)";
      console.warn(
        `[SOFTONE] Skipping outgoing loan shift for employee_code ${code} on ${date}: Foxline employee_id (PRSN) not resolved`
      );
      continue;
    }
    const hostStore = String(row.host_store || "").trim();
    const loanLocationId =
      row.loan_location_id != null
        ? Number(row.loan_location_id)
        : hostStore
          ? storeToFoxlineId[hostStore]
          : null;
    if (loanLocationId == null || !Number.isFinite(loanLocationId)) {
      console.warn(
        `[SOFTONE] Skipping outgoing loan shift on ${date}: no Foxline location for host store ${hostStore || "(unknown)"}`
      );
      continue;
    }
    const from_hour = String(row.shift_start || "").slice(0, 5);
    const to_hour = String(row.shift_end || "").slice(0, 5);
    if (!from_hour || !to_hour) continue;
    shifts.push({
      employee_id: employeeId,
      date,
      series: SOFTONE_SERIES.SHIFT,
      from_hour,
      to_hour,
      end_date: softoneEndDateForShift(date, from_hour, to_hour),
      loan_location_id: loanLocationId,
    });
  }
  return shifts;
}


export function formatSoftoneSyncSummary(result, lang) {
  const submitted = Number(result?.submitted) || 0;
  const failed = Number(result?.failed) || 0;
  const skippedDedup = Number(result?.skipped_dedup) || 0;

  if (lang === "gr") {
    const parts = [`${submitted} βάρδιες συγχρονίστηκαν`];
    if (skippedDedup > 0) {
      parts.push(`${skippedDedup} ήδη συγχρονισμένες (παραλείφθηκαν)`);
    }
    parts.push(`${failed} απέτυχαν`);
    return parts.join(" · ");
  }

  const parts = [`${submitted} shifts synced`];
  if (skippedDedup > 0) {
    parts.push(`${skippedDedup} already synced (skipped)`);
  }
  parts.push(`${failed} failed`);
  return parts.join(" · ");
}

export function softoneSyncSuccessFootnote(result, lang) {
  const submitted = Number(result?.submitted) || 0;
  const failed = Number(result?.failed) || 0;
  const skippedDedup = Number(result?.skipped_dedup) || 0;
  const errors = result?.errors || [];

  if (failed > 0 || errors.length > 0) return null;

  if (lang === "gr") {
    if (submitted === 0 && skippedDedup > 0) {
      return "Όλες οι βάρδιες ήταν ήδη συγχρονισμένες — δεν στάλθηκαν νέα έγγραφα.";
    }
    if (skippedDedup > 0) {
      return `${skippedDedup} βάρδιες παραλείφθηκαν (ήδη συγχρονισμένες από άλλο κατάστημα).`;
    }
    return "Όλες οι βάρδιες στάλθηκαν επιτυχώς.";
  }

  if (submitted === 0 && skippedDedup > 0) {
    return "All shifts were already synced — no new documents submitted.";
  }
  if (skippedDedup > 0) {
    return `${skippedDedup} shifts skipped (already synced by another store).`;
  }
  return "All shifts submitted successfully.";
}

export function formatSoftoneSkippedDedupItemLabel(item, lang) {
  const name =
    String(item?.employee_name || "").trim() ||
    String(item?.employee_code || "").trim() ||
    (item?.employee_id != null
      ? lang === "gr"
        ? `Υπάλληλος ${item.employee_id}`
        : `Employee ${item.employee_id}`
      : lang === "gr"
        ? "Υπάλληλος"
        : "Employee");
  const rawDate = String(item?.date || item?.shift_date || "").slice(0, 10);
  let displayDate = rawDate;
  if (rawDate) {
    const parsed = new Date(`${rawDate}T12:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      displayDate =
        lang === "gr"
          ? parsed.toLocaleDateString("el-GR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : rawDate;
    }
  }
  const store = String(item?.synced_via_store || "").trim();
  if (lang === "gr") {
    return store
      ? `${name} · ${displayDate} — ήδη συγχρονίστηκε μέσω ${store}`
      : `${name} · ${displayDate} — ήδη συγχρονισμένη (παραλείφθηκε)`;
  }
  return store
    ? `${name} · ${displayDate} — already synced via ${store}`
    : `${name} · ${displayDate} — already synced (skipped)`;
}
